// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { loadMessageIndex } from "@/lib/edit/message-index"
import { seedCmsDraft } from "@/lib/edit/seed-cms-draft"
import type { MessageIndex } from "@/scripts/build-message-index"
import type { Provenance, CmsProvenance } from "./provenance"
import { resolveEditTarget } from "./resolve-edit-target"
import { routeCmsEdit, routeReopen } from "./cms-edit-route"
import { DraftBanner } from "./draft-banner"
import { EditOverlay } from "./edit-overlay"
import { MobileTapCapture } from "./mobile-tap-capture"
import { useIsMobile } from "./use-is-mobile"
import { LocaleGridModal, CmsEditModal } from "./locale-grid-modal"
import { LexicalBodyModal } from "./lexical/lexical-body-modal"
import { EditStringPanel } from "./edit-string-panel"
import {
  resolveExperiment,
  type ExperimentProvenance,
} from "./experiment-provenance"
import { VariantSwitcher } from "./variant-switcher"
import {
  useCart,
  dispatchCart,
  cmsCartKey,
  countChanges,
  type CartItem,
} from "./edit-cart"
import { getMessageFile } from "@/i18n/config"
import type { LexicalContent } from "@/lib/cms"
import { locateCartNode } from "./locate-cart-node"
import {
  listEditRegions,
  subscribeEditRegions,
} from "@/lib/edit/edit-region-registry"
import {
  listExperimentRegions,
  subscribeExperimentRegions,
} from "@/lib/edit/experiment-region-registry"
import { injectRegionStamps } from "./inject-cms-stamps"
import { injectI18nStamps, stripI18nStamps } from "./inject-i18n-stamps"
import { setStampMode } from "@/lib/intl/stamp-mode"
import {
  applyPreview,
  restorePreview,
  previewEntryFor,
  type PreviewEntry,
} from "./preview-controller"
import {
  isInlineEligible,
  createInlineEditSession,
  type InlineEditSession,
} from "./inline-edit-session"

// Remembers the DOM node each cart key was picked from, so preview can inject
// the staged text back into the right element.
const elByKey = new Map<string, HTMLElement>()

/**
 * Root of the in-context editor. Mounted by the cockpit (`cockpit.tsx` /
 * `mobile-cockpit.tsx`), which `StaffEditorGate` lazy-loads for signed-in
 * staff. Owns: lazy index load, the hover overlay, the locale-grid modal, and
 * edit-preview mode + its "not pushed yet" banner. The staged-edits cart now
 * lives in the cockpit (CockpitCart), not here.
 */
export function EditModeHost({
  active,
  identity,
  preview,
  onTogglePreview,
  onRegisterReopen,
  onEditPanelActiveChange,
  editSlot,
}: {
  active: boolean
  identity: { idToken: string; environment: string; branch?: string }
  preview: boolean
  onTogglePreview: (v: boolean) => void
  onRegisterReopen?: (fn: (item: CartItem) => void) => void
  /** Fired when the in-context string editor opens / closes. The mobile sheet
   *  uses it to expand from peek so a tapped string's editor is actually visible. */
  onEditPanelActiveChange?: (open: boolean) => void
  /** Cockpit Edit-tab body node to portal the in-context editor / hint into. */
  editSlot?: HTMLElement | null
}) {
  const [index, setIndex] = useState<MessageIndex | null>(null)
  const [modal, setModal] = useState<Provenance | null>(null)
  const [cmsModal, setCmsModal] = useState<CmsProvenance | null>(null)
  // Whole-field Lexical (rich-text body) edits open a dedicated modal editor —
  // the textarea panel can't handle trees. (AHD-7515)
  const [lexicalModal, setLexicalModal] = useState<CmsProvenance | null>(null)
  // When reopening a staged lexical item, the staged tree to seed the editor
  // with (the user's in-progress edit) instead of refetching the draft.
  const [lexicalSeed, setLexicalSeed] = useState<LexicalContent | null>(null)
  const [seedText, setSeedText] = useState("")
  const [cmsOriginalOld, setCmsOriginalOld] = useState<string | undefined>(
    undefined
  )
  const [existingItem, setExistingItem] = useState<CartItem | undefined>(
    undefined
  )
  const [pickedExp, setPickedExp] = useState<ExperimentProvenance | undefined>(
    undefined
  )
  const [pickedEl, setPickedEl] = useState<HTMLElement | null>(null)
  // Large strings open the full modal (set by the panel's "Edit in full editor"
  // button); small ones edit inline in the rail.
  const [fullOpen, setFullOpen] = useState(false)
  // Original on-page text of the picked node, captured at pick time so a live
  // edit can be rolled back if the user dismisses without staging it.
  const livePreviewOrig = useRef<string | null>(null)
  const [showVariants, setShowVariants] = useState(false)
  const [forced, setForced] = useState<{ flag: string; variant: string }[]>([])
  // Bumped after re-locating staged nodes on mount, to recompute the preview.
  const [relocated, setRelocated] = useState(0)
  // Bumped when editable CMS regions register/unregister (mount / navigation),
  // to (re)run the prod stamp injection below.
  const [regionTick, setRegionTick] = useState(0)
  const [draftDiffers, setDraftDiffers] = useState(false)
  // Value typed directly into the page node, mirrored into the panel's focus
  // locale (DOM→panel). Null when no inline session is active.
  const [externalFocusValue, setExternalFocusValue] = useState<string | null>(
    null
  )
  // The live inline-edit session (contentEditable on the picked node), if any.
  const inlineSession = useRef<InlineEditSession | null>(null)
  // The panel's commit fn, registered so an inline Enter can stage via the
  // panel's own logic (no duplication).
  const panelCommit = useRef<(() => void) | null>(null)
  const items = useCart()
  const isMobile = useIsMobile()

  useEffect(() => {
    if (active && !index) loadMessageIndex().then(setIndex)
  }, [active, index])

  // Tell the host (the mobile sheet) when a string editor is open, so a tap at
  // peek height can expand the sheet enough to show it.
  useEffect(() => {
    onEditPanelActiveChange?.(!!(modal || cmsModal || lexicalModal))
  }, [modal, cmsModal, lexicalModal, onEditPanelActiveChange])

  useEffect(() => subscribeEditRegions(() => setRegionTick((n) => n + 1)), [])
  useEffect(
    () => subscribeExperimentRegions(() => setRegionTick((n) => n + 1)),
    []
  )

  // Stamp injection: the byte-clean build ships no data-cms-* attrs. Once a
  // signed-in user opens the editor we stamp each registered region from the
  // value it already rendered — scalar/html stamp the element, lexical walks the
  // in-memory tree. No network: stamping is on the critical path and must be
  // robust/offline. injectRegionStamps is idempotent (skips already-stamped
  // regions), so re-running on region register/unregister is safe. (AHD-7396)
  useEffect(() => {
    if (!active) return
    for (const region of listEditRegions()) injectRegionStamps(region)
    // A/B experiment arms: stamp data-experiment/data-variant on activation so
    // the byte-clean public build ships no attrs. resolveExperiment() reads
    // these the same as the old SSR-emitted stamps. Idempotent — skip already
    // stamped regions. (AHD-7396)
    for (const r of listExperimentRegions()) {
      if (r.el.getAttribute("data-experiment") === r.flag) continue
      r.el.setAttribute("data-experiment", r.flag)
      r.el.setAttribute("data-variant", r.variant)
    }
  }, [active, regionTick])

  // next-intl render-key stamps: flip the i18n wrapper into stamp mode while the
  // editor is active. That re-renders every `t()` consumer with an invisible
  // render-key payload (byte-clean public build stamps nothing — server t() is
  // passthrough and stamp mode is off until here). On teardown, strip mode +
  // any stamps so the DOM returns to exactly what shipped. (AHD-7514)
  useEffect(() => {
    if (!active) return
    setStampMode(true)
    return () => {
      setStampMode(false)
      stripI18nStamps(document.body)
    }
  }, [active])

  // After the stamp-mode re-render commits, read the payloads off the DOM into
  // `data-i18n-key` and scrub them, so resolveEditTarget resolves a click to the
  // exact stamped key (skipping the value→key guess + "on this page" radio).
  // Runs once the index is loaded (so a decoded key is validated against the
  // catalog). A MutationObserver re-runs it for anything rendered AFTER
  // activation — opened menus, expand/collapse, SPA navigation — debounced to a
  // rAF. injectI18nStamps is idempotent, so re-runs only stamp new payloads.
  // (AHD-7514)
  useEffect(() => {
    if (!active || !index) return
    let raf = 0
    const run = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => injectI18nStamps(document.body, index))
    }
    run()
    const observer = new MutationObserver(run)
    // characterData is essential: flipping stamp mode re-renders `t()` consumers,
    // which React applies by mutating existing text nodes' values (not adding
    // nodes) — a characterData mutation. Without it the payloads land but
    // re-injection never fires. childList/subtree covers content rendered later
    // (opened menus, SPA nav). (AHD-7514)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    })
    return () => {
      observer.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [active, index])

  // Best-effort: warn when a lexical body has a newer unpublished draft than the
  // published version the page rendered. Off the critical path — failures are
  // swallowed and just don't raise the banner. (AHD-7396)
  useEffect(() => {
    if (!active) return
    let cancelled = false
    const lexicals = listEditRegions().filter(
      (r) => r.coords.kind === "lexical"
    )
    Promise.all(
      lexicals.map(async (r) => {
        try {
          const qs = new URLSearchParams({
            collection: r.coords.collection,
            id: r.coords.id,
            field: r.coords.field,
            locale: r.coords.locale,
          })
          const res = await fetch(`/api/edit/cms-field?${qs}`, {
            headers: { "User-Authorization": `Bearer ${identity.idToken}` },
          })
          if (!res.ok) return false
          const { value } = (await res.json()) as { value: unknown }
          return JSON.stringify(value) !== JSON.stringify(r.value)
        } catch {
          return false
        }
      })
    ).then((diffs) => {
      if (!cancelled) setDraftDiffers(diffs.some(Boolean))
    })
    return () => {
      cancelled = true
    }
  }, [active, regionTick, identity.idToken])

  // After a refresh the cart reloads from localStorage but elByKey is empty.
  // Re-find each staged item's node so preview re-applies and re-clicking the
  // element recovers the staged edit instead of missing. (AHD-7396)
  useEffect(() => {
    if (!active || !items.length) return
    const segs = window.location.pathname.split("/").filter(Boolean)
    const locale = getMessageFile(segs[1] ?? "en", segs[0] ?? "ch")
    const added = items
      .filter((item) => !elByKey.has(item.key))
      .map((item) => {
        const el = locateCartNode(item, locale)
        if (el) elByKey.set(item.key, el)
        return el
      })
      .filter(Boolean).length
    if (added) setRelocated((n) => n + 1)
  }, [active, items])

  // Read any forced A/B overrides written by the variant switcher, so the
  // banner can warn that the page is rendering a non-default arm.
  useEffect(() => {
    if (!active) return
    try {
      const out: { flag: string; variant: string }[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key?.startsWith("ahead-flag:")) {
          const variant = localStorage.getItem(key)
          if (variant)
            out.push({ flag: key.slice("ahead-flag:".length), variant })
        }
      }
      setForced(out)
    } catch {}
  }, [active])

  // The preview banner is shown when preview mode has something to announce.
  const bannerShown =
    active && preview && (items.length > 0 || forced.length > 0)
  // Reflow the page down by the banner height instead of letting the fixed
  // banner overlay (and hide) the top string. (AHD-7494)
  useEffect(() => {
    document.documentElement.classList.toggle(
      "ahead-edit-banner-open",
      bannerShown
    )
    return () =>
      document.documentElement.classList.remove("ahead-edit-banner-open")
  }, [bannerShown])

  const entries = useMemo<PreviewEntry[]>(
    () =>
      items
        .map((i) => previewEntryFor(i, elByKey.get(i.key)))
        .filter((e): e is PreviewEntry => e !== null),
    // `relocated` isn't read in the body but IS a real dependency: the memo reads
    // the mutable `elByKey` Map, which the relocation effect populates after a
    // refresh. Bumping `relocated` is the signal to recompute once nodes are found.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, relocated]
  )

  useEffect(() => {
    if (!active || !preview) return
    applyPreview(entries)
    return () => restorePreview(entries)
  }, [active, preview, entries])

  // A node is editable if it's already staged — preview may have rewritten its
  // text so the value→key lookup would miss, but we still want to reopen it.
  const isStagedNode = useCallback(
    (el: HTMLElement) => Array.from(elByKey.values()).some((n) => n === el),
    // elByKey is a mutable Map repopulated as items are staged / relocated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, relocated]
  )

  // Drives the overlay's honest affordance: outline + pencil only when the
  // element resolves to a real CMS/intl target (or is already staged). (AHD-7410)
  const canEdit = useCallback(
    (el: HTMLElement) =>
      resolveEditTarget(el, window.location.pathname, index) !== null ||
      isStagedNode(el),
    [index, isStagedNode]
  )

  const inlineEligible = useCallback(
    (el: HTMLElement) =>
      !isMobile &&
      isInlineEligible(
        el,
        resolveEditTarget(el, window.location.pathname, index),
        index,
        getMessageFile(
          window.location.pathname.split("/").filter(Boolean)[1] ?? "en",
          window.location.pathname.split("/").filter(Boolean)[0] ?? "ch"
        )
      ),
    [index, isMobile]
  )

  // Expose reopenItem to the parent (the cockpit cart) so it can reopen staged
  // items for editing. Registered once; reopenItem is a hoisted closure over
  // stable setters. Must sit ABOVE the early return so the hook order is stable.
  useEffect(() => {
    onRegisterReopen?.(reopenItem)
    // reopenItem closes over state setters that are stable for the host's life
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Start an in-DOM edit session whenever a freshly picked node is inline-
  // eligible. Keyed on the picked element + which modal opened, so re-picks and
  // closes recreate/destroy correctly. Mobile and panel-only picks skip this.
  useEffect(() => {
    const el = pickedEl
    if (!el || !(modal || cmsModal)) return
    if (!inlineEligible(el)) return
    setExternalFocusValue(null)
    // Capture the pre-edit text so Esc (and any dismiss) can roll the node back —
    // typing directly in the node never goes through `liveEdit`, so without this
    // `rollbackLiveEdit` has nothing to restore and the page copy stays edited.
    livePreviewOrig.current = el.textContent ?? ""
    const session = createInlineEditSession({
      el,
      onInput: (text) => setExternalFocusValue(text),
      onEnter: () => panelCommit.current?.(),
      onEscape: () => closePick(),
      // Leaving the node (e.g. clicking into the panel) ends the in-DOM session
      // so it isn't left contentEditable; the mirrored value stays as a dirty
      // panel draft and panel→DOM `liveEdit` keeps working from there.
      onBlur: () => endInlineSession(),
    })
    inlineSession.current = session
    return () => {
      session.destroy()
      if (inlineSession.current === session) inlineSession.current = null
    }
    // closePick/inlineEligible are stable enough for the pick's lifetime; we key
    // the effect on the identity of the pick (element + open modal).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedEl, modal, cmsModal])

  if (!active) return null

  const page = typeof window !== "undefined" ? window.location.pathname : "/"
  // Message-file locale for the current page (e.g. /de/de → de-DE, /ch/de →
  // de-CH) — the grid textarea to autofocus so you can start typing the locale
  // you're viewing. This indexes the message index, which is keyed by
  // message-file id, so it must be getMessageFile and not the CMS locale.
  const segs = page.split("/").filter(Boolean)
  const currentLocale = getMessageFile(segs[1] ?? "en", segs[0] ?? "ch")

  // When the pick is edited in place, the contentEditable node should hold the
  // caret — so tell the panel NOT to autofocus its textarea (it would steal focus
  // from the page node the moment it mounts). Panel-only picks keep autofocus.
  const inlineActive = !!(pickedEl && inlineEligible(pickedEl))

  // Live-write the focus-locale draft into the picked node as the user types, so
  // the change shows on the left while editing on the right. The original is
  // captured on the first keystroke so a dismiss can roll it back.
  function liveEdit(value: string) {
    const el = pickedEl
    if (!el) return
    if (livePreviewOrig.current === null)
      livePreviewOrig.current = el.textContent ?? ""
    el.textContent = value
    el.style.boxShadow = "inset 0 -2px 0 #2b6cff"
  }
  // Roll back the picked node's uncommitted live edit. Leaves it if the edit was
  // committed (the cart-preview effect owns it then) or already staged.
  function rollbackLiveEdit(committed?: boolean) {
    const el = pickedEl
    if (el) {
      const staged = items.some((i) => elByKey.get(i.key) === el)
      if (!committed && !staged && livePreviewOrig.current !== null)
        el.textContent = livePreviewOrig.current
      el.style.removeProperty("box-shadow")
    }
    livePreviewOrig.current = null
  }
  // Tear down the live inline session (if any) and reset its bridge state.
  function endInlineSession() {
    inlineSession.current?.destroy()
    inlineSession.current = null
    panelCommit.current = null
    setExternalFocusValue(null)
  }
  // Close the in-panel editor.
  function closePick(committed?: boolean) {
    endInlineSession()
    rollbackLiveEdit(committed)
    setModal(null)
    setCmsModal(null)
    setLexicalModal(null)
    setLexicalSeed(null)
    setFullOpen(false)
  }

  // The staged cart item an intl element belongs to (text may have changed under
  // preview, so the value→key lookup would miss — recover the key via elByKey).
  function existingIntlItemFor(el: HTMLElement): CartItem | undefined {
    const hit = Array.from(elByKey.entries()).find(([, node]) => node === el)
    if (!hit) return undefined
    return items.find((i) => i.key === hit[0] && i.source === "intl")
  }

  // Reopen the editor for a staged cart item (from the cart list), seeded with
  // your change so you can refine or correct it.
  function reopenItem(item: CartItem) {
    const el = elByKey.get(item.key) ?? null
    setFullOpen(false)
    setLexicalModal(null)
    setLexicalSeed(null)
    livePreviewOrig.current = null
    setPickedEl(el)
    setPickedExp(item.experiment)
    const route = routeReopen(item)
    // A staged whole-field Lexical edit must reopen in the Lexical modal seeded
    // with the staged tree — routing it to the scalar panel would re-stage it as
    // a placeholder string and corrupt the published body (see routeReopen).
    if (route.surface === "lexical" && item.cms) {
      setExistingItem(undefined)
      setCmsModal(null)
      setModal(null)
      setLexicalSeed(route.seed)
      setLexicalModal({
        status: "cms",
        kind: "lexical",
        collection: item.cms.collection,
        id: item.cms.id,
        field: item.cms.field,
        locale: route.locale,
      })
    } else if (route.surface === "cms" && item.cms) {
      const locale = Object.keys(item.values)[0] ?? currentLocale
      const v = item.values[locale]
      setExistingItem(undefined)
      setSeedText(v?.new ?? "")
      setCmsOriginalOld(v?.old)
      setModal(null)
      setCmsModal({
        status: "cms",
        kind: "scalar",
        collection: item.cms.collection,
        id: item.cms.id,
        field: item.cms.field,
        locale,
      })
    } else {
      setExistingItem(item)
      setCmsModal(null)
      setModal({ status: "exact", key: item.key })
    }
  }

  // Picks a tapped/clicked editable element and routes it to the right editor
  // surface. Shared by the desktop hover overlay and the mobile tap-capture.
  function pick(el: HTMLElement) {
    // New pick: roll back any uncommitted live edit on the previous node
    // and reset to inline mode before switching to the new target.
    rollbackLiveEdit()
    endInlineSession()
    setFullOpen(false)
    setLexicalModal(null)
    setLexicalSeed(null)
    const exp = resolveExperiment(el) ?? undefined
    const target = resolveEditTarget(el, page, index)
    // CMS surfaces win: an exact data-cms-* hit never goes through the
    // next-intl value→key guess.
    if (target?.kind === "cms") {
      const cms = target.cms
      const route = routeCmsEdit(cms)
      // Rich-text body fields open the whole-field Lexical modal — the textarea
      // panel can't represent a tree. (AHD-7515)
      if (route.surface === "lexical") {
        setPickedEl(el)
        setPickedExp(exp)
        // Re-clicking an already-staged body reopens it seeded with the staged
        // tree, not the refetched draft — same as reopening from the cart.
        const staged = items.find((i) => i.key === cmsCartKey(cms))?.fullValue
        setLexicalSeed(staged ?? null)
        setLexicalModal(cms)
        return
      }
      // If this field is already staged, reopen seeded with the staged
      // value (preview rewrote the DOM text) and preserve the real `old`.
      const cartKey = cmsCartKey(cms)
      const staged = items.find((i) => i.key === cartKey)?.values[cms.locale]
      setPickedEl(el)
      setPickedExp(exp)
      setExistingItem(undefined)
      // textContent fallback: innerText is "" for a not-yet-painted node.
      setSeedText(
        staged?.new ?? (el.innerText?.trim() || el.textContent?.trim() || "")
      )
      setCmsOriginalOld(staged?.old)
      // Best-effort: seed a not-yet-staged field with its latest CMS draft so an
      // internal user continues prior unpublished edits. Off the critical path —
      // any failure keeps the DOM seed. (Lexical fields returned above.)
      if (!staged) {
        seedCmsDraft(
          {
            collection: cms.collection,
            id: cms.id,
            field: cms.field,
            locale: cms.locale,
          },
          identity.idToken
        ).then((draft) => {
          if (draft != null) setSeedText(draft)
        })
      }
      setCmsModal(cms)
      // An html field (e.g. the long FAQ answer) opens straight in the
      // full-screen editor — the cramped rail textarea can't hold the whole
      // answer. Scalars keep the in-rail panel. (AHD-7515)
      if (route.fullOpen) setFullOpen(true)
      return
    }
    // Re-clicking an already-edited string: preview changed its text, so
    // recover the key from elByKey and reopen seeded with your change.
    const existing = existingIntlItemFor(el)
    if (existing) {
      setPickedEl(el)
      setPickedExp(exp)
      setExistingItem(existing)
      setModal({ status: "exact", key: existing.key })
      return
    }
    // Fresh next-intl hit. The overlay gates on `canEdit`, so a non-target
    // never reaches here, but stay defensive if it somehow does.
    if (target?.kind !== "intl") return
    setPickedEl(el)
    setPickedExp(exp)
    setExistingItem(undefined)
    setModal(target.provenance)
  }

  return (
    <div data-ahead-editor-ui>
      <DraftBanner show={draftDiffers} />
      {bannerShown ? (
        // Clipped to the content column + single-line via .ahead-edit-preview-banner
        // (AHD-7487); reflows the page down via .ahead-edit-banner-open (AHD-7494)
        // so it never sits over the rail, overflows, or hides the top string.
        <div className="ahead-edit-preview-banner">
          <span>
            {items.length
              ? `Preview — ${countChanges(items)} unsaved change${countChanges(items) > 1 ? "s" : ""} shown · not pushed yet`
              : "Editing"}
            {forced.length
              ? ` · ⚗ variant forced: ${forced.map((f) => `${f.flag}=${f.variant}`).join(", ")}`
              : null}
          </span>
          <button onClick={() => onTogglePreview(false)}>Exit preview</button>
        </div>
      ) : null}

      {isMobile ? (
        <MobileTapCapture active={active} canEdit={canEdit} onPick={pick} />
      ) : (
        <EditOverlay
          active={active}
          canEdit={canEdit}
          inlineEligible={inlineEligible}
          onPick={pick}
        />
      )}

      {/* In-context editor: lives in the cockpit Edit-tab body (portal), not a
          page-covering modal, so the render stays visible while you edit. The
          hint shows when nothing is picked. */}
      {editSlot
        ? createPortal(
            modal || cmsModal ? (
              <EditStringPanel
                // Remount on every new pick so the panel's prop-seeded state
                // (resolved key, drafts) refreshes — without this the second
                // string clicked would keep showing the first one. (AHD-7487)
                key={
                  cmsModal
                    ? cmsCartKey(cmsModal)
                    : modal?.status === "exact"
                      ? `intl:${modal.key}`
                      : modal?.status === "ambiguous"
                        ? `amb:${modal.candidates.join(",")}`
                        : "miss"
                }
                provenance={modal}
                cmsProvenance={cmsModal}
                index={index}
                page={page}
                // When the full-screen editor is already open for this CMS field
                // (html → fullOpen, or the user clicked "Edit in full editor"),
                // collapse the rail to a hint so the field isn't shown twice.
                // (finding 2)
                fullOpen={fullOpen}
                experiment={pickedExp}
                existing={existingItem}
                cmsSeedText={seedText}
                cmsOriginalOld={cmsOriginalOld}
                focusLocale={currentLocale}
                idToken={identity.idToken}
                autoFocus={!inlineActive}
                externalFocusValue={externalFocusValue}
                onRegisterCommit={(fn) => (panelCommit.current = fn)}
                onLiveChange={liveEdit}
                onAddToCart={(item) => {
                  if (pickedEl) elByKey.set(item.key, pickedEl)
                  dispatchCart({ type: "add", item })
                }}
                onOpenFull={() => setFullOpen(true)}
                onClose={closePick}
              />
            ) : (
              <div className="ahead-cockpit-edit-hint">
                {isMobile
                  ? "Tap any text on the page to edit it."
                  : "Hover any text and click the ✎, or double-click it, to edit."}
              </div>
            ),
            editSlot
          )
        : null}

      {/* Full modal — only for huge strings, opened from the panel's "Edit in
          full editor" button (the Lexical body editor also routes here). */}
      {fullOpen && modal && index ? (
        <LocaleGridModal
          provenance={modal}
          index={index}
          page={page}
          experiment={pickedExp}
          existing={existingItem}
          focusLocale={currentLocale}
          idToken={identity.idToken}
          onAddToCart={(item) => {
            if (pickedEl) elByKey.set(item.key, pickedEl)
            dispatchCart({ type: "add", item })
          }}
          onClose={() => closePick()}
        />
      ) : null}

      {fullOpen && cmsModal ? (
        <CmsEditModal
          provenance={cmsModal}
          idToken={identity.idToken}
          page={page}
          // Reopening a staged edit seeds the textarea with the staged value;
          // a fresh open lets the modal seed from the fetched raw field value
          // (the complete value — never the truncated DOM text). cmsOriginalOld
          // is set only when reopening a staged item.
          stagedValue={cmsOriginalOld !== undefined ? seedText : undefined}
          experiment={pickedExp}
          onAddToCart={(item) => {
            if (pickedEl) elByKey.set(item.key, pickedEl)
            dispatchCart({ type: "add", item })
          }}
          onClose={() => closePick()}
        />
      ) : null}

      {/* Whole-field Lexical rich-text body editor (AHD-7515). Stages the full
          serialized tree as a CMS cart item carrying `fullValue`. The page body
          DOM isn't live-rewritten (trees can't easily map back to the rendered
          DOM) — the edit shows on publish, not in preview. */}
      {lexicalModal ? (
        <LexicalBodyModal
          provenance={lexicalModal}
          idToken={identity.idToken}
          // On reopen the host seeds the editor with the STAGED tree (the user's
          // in-progress edit), not the refetched draft.
          initialTreeOverride={lexicalSeed ?? undefined}
          onSave={(content, baseline) => {
            const { collection, id, field, locale } = lexicalModal
            const key = cmsCartKey({ collection, id, field })
            if (pickedEl) elByKey.set(key, pickedEl)
            dispatchCart({
              type: "add",
              item: {
                key,
                source: "cms",
                page,
                // Human-readable placeholder for the cart list; the real payload
                // is `fullValue`.
                values: {
                  [locale]: { old: "‹rich body›", new: "‹rich body, edited›" },
                },
                // `locale` is the write locale (from provenance, not the
                // placeholder key order); `baseline` is the draft the editor
                // opened on, so the apply can refuse a lost-update overwrite.
                cms: { collection, id, field, locale, baseline },
                fullValue: content,
                experiment: pickedExp,
              },
            })
          }}
          onClose={() => {
            setLexicalModal(null)
            closePick()
          }}
        />
      ) : null}

      {/* VariantSwitcher is desktop-only — its left-edge vertical tab and wide
          panel don't fit the mobile bottom-sheet surface. */}
      {!isMobile ? (
        <>
          {showVariants ? <VariantSwitcher page={page} /> : null}
          {/* Left-edge tab — clears the bottom-left cart and the top banner. */}
          <button
            data-ahead-editor-ui
            onClick={() => setShowVariants((v) => !v)}
            title="Render the page as a specific A/B variant"
            style={{
              position: "fixed",
              left: 0,
              top: "45%",
              zIndex: 2147483601,
              border: "1px solid #e0d6f5",
              borderLeft: "none",
              background: showVariants ? "#f1ecfb" : "#fff",
              color: "#7a4fd0",
              borderRadius: "0 9px 9px 0",
              padding: "9px 10px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 6px 18px rgba(0,0,0,.12)",
              fontFamily: "ui-sans-serif, system-ui",
              writingMode: "vertical-rl",
            }}
          >
            ⚗ {showVariants ? "Hide" : "Variants"}
          </button>
        </>
      ) : null}
    </div>
  )
}
